<template>
  <fieldset v-if="positions.length" :class="$style.group">
    <legend>
      {{ label ?? $t('positions:list.positionsLegend') }}
    </legend>
    <ul ref="listEl" :key="renderKey" :class="$style.list">
      <li
        v-for="position in positions"
        :key="position.id"
        :data-id="position.id"
        :data-position-drop-target="fileDropTarget || undefined"
      >
        <PositionListItem
          :position="position"
          :groupName="groupName"
          :isActive="position.id === activeId"
          :disableDrag
          @selected="$emit('selected', $event)"
        />
      </li>
    </ul>
  </fieldset>
  <div v-else class="text-subtle text-center mt-4 positionListEmpty">
    <h4 class="heading-label">{{ $t('positions:list.empty') }}</h4>
  </div>
</template>
<script setup lang="ts">
import { dragula } from '@/helpers/dragula';
import type { Position } from '@cutr/constants/position';
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useId,
  useTemplateRef,
  watch,
} from 'vue';
import PositionListItem from './PositionListItem.vue';

const props = defineProps<{
  positions: Position[];
  activeId?: string;
  label?: string;
  disableDrag?: boolean;
  fileDropTarget?: boolean;
}>();

const emit = defineEmits<{
  (e: 'selected', position: Position): void;
  (e: 'reordered', positions: Position[]): void;
}>();

// Shared radio `name` so all items form one keyboard-navigable group.
const groupName = useId();
const listEl = useTemplateRef<HTMLUListElement>('listEl');

// Bumping this key tears down the <ul> subtree — including any nodes dragula
// moved — and re-renders the list fresh from `positions`. Keeping data as the
// single source of truth avoids the keyed-diff desync you get from letting
// dragula mutate Vue-owned DOM nodes in place.
const renderKey = ref(0);

let drake: ReturnType<typeof dragula> | null = null;

function destroyDrake() {
  // @ts-expect-error - TODO(claudio): fix dragula typings
  drake?.destroy();
  drake = null;
}

function initDrake() {
  if (props.disableDrag) return;

  destroyDrake();
  if (!listEl.value) return;

  drake = dragula([listEl.value], {
    direction: 'vertical',
    moves: (_el: HTMLElement, _container: HTMLElement, handle: HTMLElement) =>
      !!handle?.closest('[data-drag-handle]'),
  });

  drake.on('drop', () => {
    if (!listEl.value) return;
    // dragula has already moved the node into its new slot — read that order.
    const ids = [...listEl.value.children].map(
      (li) => (li as HTMLElement).dataset.id
    );
    const byId = new Map(props.positions.map((p) => [p.id, p]));
    const reordered = ids
      .map((id) => (id ? byId.get(id) : undefined))
      .filter((p): p is Position => p !== undefined);
    if (reordered.length !== props.positions.length) return;

    // Discard dragula's in-place DOM mutation and rebuild from the new data.
    renderKey.value += 1;
    emit('reordered', reordered);
  });
}

onMounted(initDrake);
onBeforeUnmount(destroyDrake);
// Re-attach dragula to the freshly rendered <ul> after each reorder rebuild.
watch(renderKey, () => nextTick(initDrake));
watch(
  () => props.positions,
  () => nextTick(initDrake),
  { deep: true }
);
</script>
<style module>
.group {
  border: 0;
  margin: 0;
  padding: 0;
  min-inline-size: 0;
}

.group legend {
  font-size: 12px;
  line-height: 16px;
  text-transform: uppercase;
  font-weight: 500;
  padding: 0 0 0.5rem;
}

.list > li + li {
  padding-block-start: 0.5rem;
}

.list :global(.gu-transit) {
  display: none !important;
}
</style>
